import React, { memo } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
  Pagination,
  Stack,
} from '@mui/material'
import PopupComponent from '../Shared/Popup/PopupComponent'
import styles from './Tables.module.scss'
import { checkIfStringExist } from '../../utils/checkIfStringExist'
import { defaultFormatDate } from '../../utils/format'
import { getNestedObjValue } from '../../utils/table'
import { COLORS } from '../../constants/colors'
import SortIcon from '@mui/icons-material/Sort'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'

export interface TableItem {
  type: 'text' | 'date' | 'component' | 'daysPeriod'
  value?: string
  label?: string
  component?: React.ReactNode | ((row: any) => React.ReactNode)
  sortable?: boolean
}

interface RowMenuConfigFunc {
  (params: { row: any }): any
}

type ConfigTableFunc = (row?: any, history?: any, dispatch?: any) => TableItem[]

interface TableComponentProps {
  data: any[]
  history?: any
  dispatch?: any
  handleChangePage: (event: React.ChangeEvent<unknown>, page: number) => void
  selectedPage: number
  maxPageNumbers: number
  configTable: ConfigTableFunc
  rowMenuConfig?: RowMenuConfigFunc
  headerBackground?: string
  bodyBackground?: string
  paginated?: boolean
  onRowClick?: (row: any) => void
  rowClickable?: boolean
  hover?: boolean
  itemsPerPage?: number
  handleSort?: (field: string) => void
  sortField?: string | null
  sortDirection?: 'asc' | 'desc'
}

const TableComponent: React.FC<TableComponentProps> = ({
  data,
  history,
  dispatch,
  handleChangePage,
  selectedPage,
  maxPageNumbers,
  configTable,
  rowMenuConfig,
  headerBackground = COLORS.lightestGray,
  bodyBackground = COLORS.lightestGray,
  paginated = true,
  onRowClick = () => {},
  rowClickable = false,
  hover = false,
  itemsPerPage = 5,
  handleSort = () => {},
  sortField = '',
  sortDirection = 'asc',
}) => {
  const getTableCell = (item: TableItem, row: any) => {
    if (item.component && typeof item.component === 'function') {
      return item.component(row)
    }

    switch (item.type) {
      case 'text':
        return checkIfStringExist(getNestedObjValue(row, item.value))
      case 'date':
        return defaultFormatDate(getNestedObjValue(row, item.value))
      case 'daysPeriod':
        const startDate = new Date(row.start_date)
        const endDate = new Date(row.end_date)
        if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
          const diffInDays = Math.ceil(
            (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24),
          )
          return `${diffInDays} days`
        } else {
          return 'N/A'
        }
      default:
        console.log('No matching type selected')
        return <div />
    }
  }

  if (!configTable || typeof configTable !== 'function') {
    return <div>No configuration available for table</div>
  }

  return (
    <Box sx={{ overflow: 'auto' }}>
      <Box sx={{ width: '100%', display: 'table', tableLayout: 'fixed' }}>
        <Table>
          <TableHead sx={{ backgroundColor: headerBackground }}>
            <TableRow>
              {configTable().map((item, index) => (
                <TableCell key={index} className={styles.tableCell}>
                  <Box className={styles.rowWrapper}>
                    {item.label}
                    {item.sortable && (
                      <span
                        className={styles.sortIcon}
                        onClick={() => handleSort(item.value || '')}
                      >
                        {sortField === item.value ? (
                          sortDirection === 'asc' ? (
                            <ArrowUpwardIcon fontSize="small" />
                          ) : (
                            <ArrowDownwardIcon fontSize="small" />
                          )
                        ) : (
                          <SortIcon fontSize="small" />
                        )}
                      </span>
                    )}
                  </Box>
                </TableCell>
              ))}
              {rowMenuConfig && (
                <TableCell
                  sx={{ backgroundColor: headerBackground }}
                  className={styles.emptyRow}
                ></TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody sx={{ backgroundColor: bodyBackground }}>
            {data.map((row, index) => (
              <TableRow
                key={index}
                onClick={() => onRowClick(row)}
                className={`${rowClickable ? styles.fullRow : ''} ${hover ? styles.hoverRow : ''}`}
              >
                {configTable(row, history, dispatch).map((item, index) => (
                  <TableCell key={index}>{getTableCell(item, row)}</TableCell>
                ))}
                {rowMenuConfig && (
                  <TableCell>
                    <div className={styles.lastColumn}>
                      <div className={styles.popupContainer}>
                        <PopupComponent config={rowMenuConfig({ row })} />
                      </div>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {data.length === 0 && <h4 className={styles.emptyTableTitle}>No results found</h4>}
        {paginated && maxPageNumbers > 1 && (
          <div className={styles.paginationWrapper}>
            <Stack spacing={2}>
              <Pagination count={maxPageNumbers} page={selectedPage} onChange={handleChangePage} />
            </Stack>
          </div>
        )}
      </Box>
    </Box>
  )
}

export default memo(TableComponent)
